import { createSlice, current } from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";

const defaultFormInitialValues = {
  User: {
    Organisation: "",
    Name: "",
    Email: "",
    Description: "",
  },
  CurrentSubscription: null,
  SubscriptionPlans: null,
  Consumption: null,
  Billings: [],
  CanChange: false,
};

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  mode: 0,
  formKey: new Date().toString(),
  formInitialValues: { ...defaultFormInitialValues },
  formValues: { ...defaultFormInitialValues },
  formClientErrors: {},
  formServerErrors: [],
  selectedTab: 0,
  tabPosition: "top",
};

const accountManagementPageSlice = createSlice({
  name: "accountManagementPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      //payload: {processing=true, processingText=false}, when processing is false, processingText is ""
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    setFormValue: (state, action) => {
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: { ...state.formValues, [id]: value },
      };
    },

    setUserFormValue: (state, action) => {
      let id = action.payload.id;
      let value = action.payload.value;
      return {
        ...state,
        formValues: {
          ...state.formValues,
          User: { ...state.formValues.User, [id]: value },
        },
      };
    },

    setFormClientErrors: (state, action) => ({
      ...state,
      formClientErrors: { ...action.payload },
    }),

    setFormServerErrors: (state, action) => ({
      ...state,
      formServerErrors: [...action.payload],
    }),

    pageLoaded: (state, action) => { 
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const updatedPlans = action.payload.SubscriptionPlans.map((plan) => ({
        ...plan,
        IsSelected: false,
      }));

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        mode: 2,
        formKey: new Date().toString(),
        formValues: {
          User: {
            Organisation: action.payload.User.Organisation,
            Name: action.payload.User.Name,
            Email: action.payload.User.Email,
            Description: action.payload.User.Description,
          },
          CurrentSubscription: action.payload.CurrentSubscription,
          SubscriptionPlans: updatedPlans,
          Consumption: action.payload.Consumption,
          Billings: action.payload.Billings,
        },
        formInitialValues: { ...defaultFormInitialValues },
        formClientErrors: {},
        formServerErrors: [],
      };
    },

    setSubscriptionPlans: (state, action) => {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          SubscriptionPlans: action.payload,
        },
      };
    },

    setResizeSubscription: (state, action) => {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          CanChange: action.payload.CanChange,
        },
      };
    },
    setCancelSubscription: (state, action) => {
      return {
        ...state,
      };
    },

    updateUser: (state, action) => {
      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
      };
    },

    setSelectedTab: (state, action) => {
      return {
        ...state,
        selectedTab: action.payload,
      };
    },
  
    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default accountManagementPageSlice.reducer;

export const loadPage = (t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage")})
  );

  try {
    var response = await api({
      url: "subscription/getaccountinfo",
      method: "post",
      data: {},
      //headers:{'h':'1'}
    });
    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().accountManagementPage.pageDisposed) return;
    //it's ok to dispatch here
    let data = response.data.Data;
    dispatch(pageLoaded(data));
    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const updateUser = (name, description, t) => async (dispatch, getState) => {
  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage") })
  ); 
  try {
    var response = await api({
      url: "auth/updateselfuser",
      method: "post",
      data: {
        name: name,
        description: description,
      },
      //headers:{'h':'1'}
    });
    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().accountManagementPage.pageDisposed) return;
    //it's ok to dispatch here
    let data = response.data.Data;
    dispatch(updateUser(data));
    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const resizeSubscription = (id, t) => async (dispatch, getState) => {
  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage") })
  );

  try {
    var response = await api({
      url: "subscription/resizecurrentplan",
      method: "post",
      data: {
        TargetPlan: id,
      },
      //headers:{'h':'1'}
    });
    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().accountManagementPage.pageDisposed) return;
    //it's ok to dispatch here
    let data = response.data.Data;

    dispatch(setResizeSubscription(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const cancelSubscription = (email, t) => async (dispatch, getState) => {
  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage") })
  );

  try {
    var response = await api({
      url: "subscription/canceltenantsubscription",
      method: "post",
      data: {
        email: email,
      },
      //headers:{'h':'1'}
    });
    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().accountManagementPage.pageDisposed) return;
    //it's ok to dispatch here
    //let data = response.data.Data;
    dispatch(setCancelSubscription());
    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  pageLoaded,
  setFormValue,
  setUserFormValue,
  setSelectedTab, 
  setFormClientErrors,
  setFormServerErrors,
  setSubscriptionPlans,
  setResizeSubscription,
  setCancelSubscription,
  disposePage,
} = accountManagementPageSlice.actions;
